import { Bouton } from './composants/bouton'
import { useState, useSyncExternalStore, type FormEvent } from 'react'
import { ErreurCode } from '../lib/chiffrement/cles'
import { oublierCompte } from '../lib/compte'
import { creerVerrou, type EtatVerrou, type Verrou } from '../lib/chiffrement/verrou'
import { getBase } from '../outbox/base'
import { compterEnAttente } from '../outbox/depot'
import { CHAMP } from './synchro'

let verrou: Verrou | undefined

/** Verrou de la base de l'app (un par onglet). */
export function getVerrou(): Verrou {
  if (!verrou) {
    verrou = creerVerrou(getBase())
    void verrou.initialiser()
  }
  return verrou
}

export function useEtatVerrou(): EtatVerrou {
  const v = getVerrou()
  return useSyncExternalStore(v.abonner, v.etat)
}

/** Écran affiché à la place de l'app tant que les données chiffrées sont verrouillées. */
export function EcranVerrou() {
  const v = getVerrou()
  const [code, setCode] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const avecPasskey = !!v.config()?.passkey

  async function tenter(f: () => Promise<void>) {
    setEnCours(true)
    setErreur(null)
    try {
      await f()
    } catch (e) {
      setErreur(e instanceof ErreurCode ? 'Code incorrect.' : e instanceof Error ? e.message : 'Déverrouillage impossible.')
      setCode('')
    } finally {
      setEnCours(false)
    }
  }

  // Seule issue sans le code : repartir d'un appareil vide (les index restent lisibles verrouillés)
  async function effacerAppareil() {
    const enAttente = await compterEnAttente(getBase())
    const perte = enAttente > 0 ? `${enAttente} modification(s) pas encore synchronisée(s) seront perdues. ` : ''
    if (!confirm(`${perte}Effacer toutes les données de cet appareil ? Celles déjà synchronisées seront récupérées à la prochaine connexion.`)) return
    await getBase().delete()
    oublierCompte()
    location.reload()
  }

  function soumettre(e: FormEvent) {
    e.preventDefault()
    if (code) void tenter(() => v.deverrouillerParCode(code))
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 bg-white p-6 text-slate-900">
      <h1 className="text-2xl font-bold">Fil-à-Plomb</h1>
      <p>Vos données sont verrouillées sur cet appareil.</p>
      <form onSubmit={soumettre} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-slate-700">Code de déverrouillage</span>
          <input
            className={`${CHAMP} text-center text-2xl tracking-widest`}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        {erreur && (
          <p className="rounded border-2 border-red-700 bg-red-50 p-3 text-sm text-red-900" role="alert">
            {erreur}
          </p>
        )}
        <Bouton type="submit" className="flex-1" disabled={enCours || !code}>
          {enCours ? 'Vérification…' : 'Déverrouiller'}
        </Bouton>
      </form>
      {avecPasskey && (
        <Bouton variante="secondaire" disabled={enCours} onClick={() => tenter(() => v.deverrouillerParPasskey())}>
          Déverrouiller avec ma passkey
        </Bouton>
      )}
      <p className="text-sm text-slate-700">
        Code oublié : les données chiffrées de cet appareil ne peuvent pas être récupérées. Celles déjà synchronisées restent
        sur le serveur.
      </p>
      <Bouton variante="lienDanger" className="self-start text-sm" onClick={effacerAppareil}>
        Effacer les données de cet appareil
      </Bouton>
    </main>
  )
}
